import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { ExportIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { CloseModalButton } from '../../../components/close-modal-button/index';
import { ModalWrapper } from '../../../components/modal-wrapper/index';
import { StakeholderDividendPayout } from '../../../hooks/dividend/use-dividend-stakeholder-payouts.hook';
import {
  formatDividendAmount,
  formatDividendPerShare,
} from '../../dividends/utils/format-dividend-amount.util';

type Props = {
  currencySymbol: string;
  isExporting: boolean;
  onClose: () => void;
  onExport: () => void;
  open: boolean;
  payouts: StakeholderDividendPayout[];
  stakeholderName: string;
};

const headerCellClass =
  'h-11 border-b border-gray-200 px-4 py-3 text-left text-[12px] leading-[18px] font-medium text-[#172335]';
const bodyCellClass =
  'h-[72px] max-w-[240px] truncate px-4 py-4 align-middle text-sm font-[450] text-gray-700';

const formatNumber = (value: null | number): string =>
  value === null ? '—' : formatDividendPerShare(value);

export const DividendPayoutsDetailsModal: FC<Props> = ({
  currencySymbol,
  isExporting,
  onClose,
  onExport,
  open,
  payouts,
  stakeholderName,
}) => (
  <ModalWrapper
    className="w-full max-w-[1000px]"
    control={{ onOpenChange: (next) => !next && onClose(), open }}
    onInteractOutside={onClose}
    side="CENTER"
  >
    <div className="flex max-h-[80vh] w-full flex-col overflow-hidden rounded-2xl bg-white">
      <div className="flex shrink-0 items-start justify-between px-6 pb-6 pt-6">
        <div className="flex flex-col gap-1">
          <span className="text-xl font-[550] text-gray-700">{stakeholderName}</span>
          <span className="text-xs font-[450] text-gray-500">
            <AppFormattedMessage id="DIVIDEND_PAYOUTS_DETAILS" />
          </span>
        </div>
        <CloseModalButton onClose={onClose} />
      </div>

      <div className="mx-6 mb-6 flex min-h-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-200">
        <div className="flex-1 overflow-y-auto">
          <table className="w-full">
            <thead className="sticky top-0 bg-gray-50">
              <tr>
                <th className={headerCellClass}>
                  <AppFormattedMessage id="PAYOUT_NAME" />
                </th>
                <th className={headerCellClass}>
                  <AppFormattedMessage id="SHARE_CLASS" />
                </th>
                <th className={headerCellClass}>
                  <AppFormattedMessage id="SHARE_AMOUNT" />
                </th>
                <th className={headerCellClass}>
                  <AppFormattedMessage id="DIVIDEND_PER_SHARE" />
                </th>
                <th className={headerCellClass}>
                  <AppFormattedMessage id="TOTAL_PAYOUT" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payouts.map((payout, index) => (
                <tr key={`${payout.id}-${payout.shareClassName ?? ''}-${index}`}>
                  <td className={bodyCellClass} title={payout.name}>
                    {payout.name}
                  </td>
                  <td className={bodyCellClass} title={payout.shareClassName ?? undefined}>
                    {payout.shareClassName ?? '—'}
                  </td>
                  <td className={bodyCellClass}>{formatNumber(payout.stakeholderShares)}</td>
                  <td className={bodyCellClass}>{formatNumber(payout.amountPerShare)}</td>
                  <td className={twMerge(bodyCellClass, 'font-[550] text-forest-600')}>
                    {currencySymbol} {formatDividendAmount(payout.itemAmount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex h-[62px] shrink-0 items-center justify-between bg-gray-100 px-6">
        <Button
          className="h-9 w-fit bg-transparent px-4 text-sm font-[450] text-gray-700"
          onClick={onClose}
          styleType="NONE"
        >
          <AppFormattedMessage id="CLOSE" />
        </Button>
        <Button
          className="flex h-9 w-fit items-center gap-1 border border-gray-300 bg-white px-4 py-2.5 text-sm font-[450] text-gray-700"
          disabled={isExporting}
          onClick={onExport}
          styleType="DEFAULT_ROUNDED"
        >
          <ExportIcon className="size-5" />
          <AppFormattedMessage id="EXPORT" />
        </Button>
      </div>
    </div>
  </ModalWrapper>
);
